import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import axios from '../utils/axiosConfig';
import {
  clearDeltaNotifications,
  markNotificationsAsProcessed,
  setDeleteNotificationsLoading,
  setDeltaNotifications,
  setNotifications,
  setNotificationsError,
  setNotificationsLoading
} from '../redux/slices/notification';
import {AxiosError} from 'axios';
import {DeleteNotificationResponse, NotificationDto} from "../types/notification";
import {errorHandler} from '../utils/errorHandler';
import toast from "react-hot-toast";

const useNotificationService = () => {
    const dispatch = useDispatch();

    const showError = useCallback((error: any) => {
        errorHandler.showError(error);
    }, []);

    const fetchDeltaNotifications = useCallback(async (orgId: string) => {
        try {
            const response = await axios.get<NotificationDto[]>(`/notification/organization/${orgId}?notification_displayed=false`);
            dispatch(setDeltaNotifications(response.data));
        } catch (error) {
            console.error("Error fetching delta notifications:", error);
        }
    }, [dispatch]);

    const markNotificationsAsDisplayed = useCallback(async (notificationIds: number[]) => {
        try {
            await axios.put<NotificationDto[]>(`/notifications/notification_displayed_status`, {
                message_ids: notificationIds
            });
            dispatch(clearDeltaNotifications());
        } catch (error) {
            console.error("Error marking notifications as displayed:", error);
        }
    }, [dispatch]);

    const markNotificationsAsRead = useCallback(async (notificationIds: number[]) => {
        try {
            await axios.put<NotificationDto[]>(`/notifications/read`, {
                message_ids: notificationIds
            });
            dispatch(markNotificationsAsProcessed(notificationIds));
            console.log(`Marked ${notificationIds.length} notifications as read`);
        } catch (error) {
            console.error("Error reading notification:", error);
        }
    }, [dispatch]);

    const fetchNotifications = useCallback(async (org_id:any) => {
        dispatch(setNotificationsLoading(true));
        try {
            const response = await axios.get<NotificationDto[]>(`/notification/organization/${org_id}`);
            const fetchedNotifications = response.data;

            dispatch(setNotifications(fetchedNotifications));
        } catch (error) {
            console.error("Error fetching notifications:", error);
            dispatch(setNotificationsError((error as AxiosError).message || 'An unknown error occurred'));
        } finally {
            dispatch(setNotificationsLoading(false));
        }
    }, [dispatch]);

    const deleteNotification = useCallback(async (notificationId:any) => {
        dispatch(setDeleteNotificationsLoading(true));
        try {
            const response = await axios.delete<DeleteNotificationResponse>(`/notification/${notificationId}/delete`);
            toast.success(response.data.message || 'Notification deleted successfully');
        } catch (error) {
            console.error("Error deleting notification:", error);
            showError(error);
        } finally {
            dispatch(setDeleteNotificationsLoading(false));
        }
    }, [dispatch, showError]);

    return {
        fetchNotifications,
        fetchDeltaNotifications,
        markNotificationsAsRead,
        markNotificationsAsDisplayed,
        deleteNotification
    };
};

export default useNotificationService;